import React, { useState, useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { XCircle, CheckCircle2, Check, Zap } from 'lucide-react';
import { trackAddToCart, trackInitiateCheckout } from '../lib/pixel';
const BASE_PRICE = 15990;
const BUMP_PRICE = 6990;
const CHECKOUT_URL_BASE = 'https://ebookpromax.com/cart/add?id=46807051075742&quantity=1';
const CHECKOUT_URL_WITH_BUMP = 'https://ebookpromax.com/cart/add?id=46807125688478&quantity=1';
export function Offer() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [bumpSelected, setBumpSelected] = useState(false);
  const total = bumpSelected ? BASE_PRICE + BUMP_PRICE : BASE_PRICE;
  const formattedTotal = `$${total.toLocaleString('es-AR')}`;
  const toggleBump = () => {
    const next = !bumpSelected;
    setBumpSelected(next);
    if (next) {
      trackAddToCart(BUMP_PRICE, 'Kit de Herramientas - Order Bump');
    }
  };
  const handleCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    trackInitiateCheckout(total, bumpSelected ? 2 : 1);
    window.location.href = bumpSelected ? CHECKOUT_URL_WITH_BUMP : CHECKOUT_URL_BASE;
  };
  return <section id="oferta" ref={ref} className="py-20 sm:py-32 bg-[#2F4538] text-[#F5F0E8] relative overflow-hidden" data-id="element-306">
      <div className="absolute inset-0 opacity-10" data-id="element-307">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-[#B4A7D6] rounded-full blur-[150px]" data-id="element-308"></div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-309">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-310">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-[#D4AF37]" data-id="element-311">
            ¿Cuánto cuesta recuperar tu paz mental?
          </h2>
          <p className="text-xl text-[#F5F0E8]/80" data-id="element-312">
            Hagamos las cuentas juntas.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16" data-id="element-313">
          {/* El Problema */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white/5 backdrop-blur-sm p-8 rounded-3xl border border-white/10" data-id="element-314">
            <p className="text-[#F5F0E8]/90 mb-6" data-id="element-315">
              Si hoy querés resolver tu ansiedad, estrés e insomnio de forma
              natural en Argentina, tus opciones son:
            </p>
            <ul className="space-y-4 mb-8 text-sm sm:text-base text-[#F5F0E8]/80" data-id="element-316">
              <li className="flex justify-between border-b border-white/10 pb-2" data-id="element-317">
                <span data-id="element-318">Terapia (4/mes):</span>
                <span className="font-mono" data-id="element-319">$720k - $1.2M/año</span>
              </li>
              <li className="flex justify-between border-b border-white/10 pb-2" data-id="element-320">
                <span data-id="element-321">Medicación + consultas:</span>
                <span className="font-mono" data-id="element-322">$300k - $480k/año</span>
              </li>
              <li className="flex justify-between border-b border-white/10 pb-2" data-id="element-323">
                <span data-id="element-324">Masajes (2/mes):</span>
                <span className="font-mono" data-id="element-325">$240k - $432k/año</span>
              </li>
              <li className="flex justify-between border-b border-white/10 pb-2" data-id="element-326">
                <span data-id="element-327">Retiros (1/trimestre):</span>
                <span className="font-mono" data-id="element-328">$180k - $300k/año</span>
              </li>
            </ul>
            <p className="font-bold text-[#B4A7D6] mb-4" data-id="element-329">
              El problema con todo esto:
            </p>
            <ul className="space-y-2 text-[#F5F0E8]/80" data-id="element-330">
              {['Es caro', 'Dependés de horarios y turnos', 'Tenés que salir de tu casa', 'Los resultados son temporales', 'Es un gasto RECURRENTE (pagás todos los meses)'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-331">
                  <XCircle className="w-5 h-5 text-red-400/80 shrink-0" data-id="element-332" />
                  <span data-id="element-333">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* La Solución */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="bg-[#F5F0E8] p-8 rounded-3xl text-[#1a1a1a]" data-id="element-334">
            <h3 className="text-2xl font-bold text-[#2F4538] mb-6" data-id="element-335">
              Con Rituales de Pies Ancestrales:
            </h3>
            <ul className="space-y-4 mb-8" data-id="element-336">
              {['Inversión única: $15.990 (menos que 1 mes de terapia)', 'Acceso de por vida (sin costos recurrentes)', 'En tu casa, con tus tiempos', 'Resultados desde la primera semana', 'Sistema completo (no solo información)'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-337">
                  <CheckCircle2 className="w-6 h-6 text-[#8B9D83] shrink-0" data-id="element-338" />
                  <span className="font-medium" data-id="element-339">{item}</span>
                </li>)}
            </ul>

            <div className="bg-white p-6 rounded-2xl border border-[#D4AF37]/30" data-id="element-340">
              <p className="font-bold text-[#2F4538] mb-4" data-id="element-341">
                💰 VALOR REAL DEL SISTEMA:
              </p>
              <ul className="space-y-2 text-sm text-[#1a1a1a]/70 mb-4" data-id="element-342">
                <li className="flex justify-between gap-2" data-id="element-343">
                  <span className="notranslate" translate="no" lang="es" data-id="element-344">
                    Sistema E.R.A. (100+ rituales):
                  </span>
                  <span data-id="element-345">$35.000</span>
                </li>
                <li className="flex justify-between gap-2" data-id="element-346">
                  <span data-id="element-347">Oraciones y Afirmaciones:</span>
                  <span data-id="element-348">$8.500</span>
                </li>
                <li className="flex justify-between gap-2" data-id="element-349">
                  <span data-id="element-350">Audio de Meditación Profesional:</span>
                  <span data-id="element-351">$12.000</span>
                </li>
                <li className="flex justify-between gap-2" data-id="element-352">
                  <span data-id="element-353">Calendario Lunar 2026:</span>
                  <span data-id="element-354">$7.500</span>
                </li>
                <li className="flex justify-between gap-2" data-id="element-355">
                  <span data-id="element-356">Protocolo Reset de 7 Días:</span>
                  <span data-id="element-357">$15.000</span>
                </li>
              </ul>
              <div className="border-t border-[#D4AF37]/30 pt-4 flex justify-between items-center font-bold text-lg" data-id="element-358">
                <span className="text-[#2F4538]" data-id="element-359">VALOR TOTAL:</span>
                <span className="text-red-500 line-through" data-id="element-360">$78.000</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ORDER BUMP — pulsing/beating to draw attention */}
        <motion.button type="button" onClick={toggleBump} initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.5
      }} className={`relative block w-full text-left max-w-3xl mx-auto mb-8 rounded-3xl overflow-hidden border-4 transition-all duration-500 ${bumpSelected ? 'border-[#D4AF37] bg-[#FFFBE8]' : 'border-[#D4AF37] bg-[#FFFBE8] animate-bump-beat hover:animate-none'}`} style={{
        boxShadow: bumpSelected ? '0 0 0 6px rgba(212,175,55,0.25), 0 20px 60px rgba(212,175,55,0.4)' : '0 10px 40px rgba(212,175,55,0.35)'
      }} data-id="element-361">
          {/* Flash effect overlay (only when not selected) */}
          {!bumpSelected && <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl" aria-hidden data-id="element-362">
              <div className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent animate-flash" data-id="element-363"></div>
            </div>}

          {/* "Click here" pulsing arrow indicator (only when not selected) */}
          {!bumpSelected && <div className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-20 items-center gap-1 pointer-events-none" aria-hidden data-id="element-364">
              <div className="bg-[#D4AF37] text-[#2F4538] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg animate-pulse whitespace-nowrap" data-id="element-365">
                👉 Hacé click
              </div>
            </div>}

          {/* "OFERTA ÚNICA" ribbon */}
          <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-bl-2xl flex items-center gap-1 z-10" data-id="element-366">
            <Zap className="w-3.5 h-3.5" data-id="element-367" />
            Solo en esta página
          </div>

          <div className="relative z-10 flex flex-col sm:flex-row gap-6 p-6 sm:p-8 pt-10 sm:pt-8" data-id="element-368">
            {/* Image */}
            <div className="w-full sm:w-44 aspect-square shrink-0 rounded-2xl overflow-hidden bg-white shadow-md" data-id="element-369">
              <img src="https://cdn.magicpatterns.com/uploads/7KkyMvyJNWDbjzh9jj77U8/PORTADAS_HERRAMIENTAS_ORDER_BUMP.png" alt="Kit de Herramientas - Las 5 Plantillas" width={400} height={400} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-370" />
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col min-w-0" data-id="element-371">
              <div className="flex items-start gap-3 mb-3" data-id="element-372">
                {/* Custom checkbox */}
                <div className={`shrink-0 w-7 h-7 mt-0.5 rounded-md border-2 transition-all flex items-center justify-center ${bumpSelected ? 'bg-[#D4AF37] border-[#D4AF37] scale-110' : 'bg-white border-[#2F4538]/40 hover:border-[#D4AF37]'}`} aria-hidden data-id="element-373">
                  {bumpSelected && <Check className="w-5 h-5 text-white" strokeWidth={3} data-id="element-374" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-375">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#B4A7D6] mb-1" data-id="element-376">
                    ¡Sí! Sumá a mi pedido →
                  </p>
                  <h4 className="text-xl sm:text-2xl font-bold text-[#2F4538] leading-tight" data-id="element-377">
                    Kit de Herramientas "Listo para Usar"
                  </h4>
                  <p className="text-sm font-semibold text-[#2F4538]/80 mt-1" data-id="element-378">
                    Las 5 Plantillas que Convierten el Conocimiento en Acción
                  </p>
                </div>
              </div>

              <p className="text-sm text-[#1a1a1a]/70 mb-4 leading-relaxed" data-id="element-379">
                Porque de nada sirve tener 100 rituales si no sabés por dónde
                empezar. Incluye{' '}
                <strong className="font-bold text-[#2F4538]" data-id="element-380">
                  Planificador Semanal
                </strong>
                ,{' '}
                <strong className="font-bold text-[#2F4538]" data-id="element-381">
                  Lista Maestra de Ingredientes
                </strong>
                ,{' '}
                <strong className="font-bold text-[#2F4538]" data-id="element-382">
                  Tracker de 30 días
                </strong>
                ,{' '}
                <strong className="font-bold text-[#2F4538]" data-id="element-383">
                  Recetario de Mezclas
                </strong>{' '}
                y{' '}
                <strong className="font-bold text-[#2F4538]" data-id="element-384">
                  Guía de Emergencias
                </strong>
                .
              </p>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-[#D4AF37]/30 mt-auto" data-id="element-385">
                <div className="flex items-baseline gap-2 flex-wrap" data-id="element-386">
                  <span className="text-sm text-[#1a1a1a]/40 line-through" data-id="element-387">
                    $11.990
                  </span>
                  <span className="text-2xl font-bold text-[#2F4538]" data-id="element-388">
                    + $6.990
                  </span>
                </div>
                <span className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${bumpSelected ? 'bg-[#8B9D83] text-white' : 'bg-[#D4AF37]/20 text-[#2F4538]'}`} data-id="element-389">
                  {bumpSelected ? '✓ AGREGADO' : 'AHORRÁS 42%'}
                </span>
              </div>
            </div>
          </div>
        </motion.button>

        {/* CTA Box */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="bg-white rounded-[3rem] p-8 sm:p-12 text-center shadow-2xl border-4 border-[#D4AF37]/20 max-w-3xl mx-auto relative" data-id="element-390">
          <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-red-500 text-white px-6 py-2 rounded-full font-bold text-sm tracking-wider uppercase shadow-lg animate-pulse" data-id="element-391">
            🔥 Precio de Lanzamiento
          </div>

          <motion.div key={total} initial={{
          scale: 0.9,
          opacity: 0.5
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.4,
          type: 'spring'
        }} className="text-6xl sm:text-7xl font-bold text-[#B4A7D6] mb-2 mt-4" data-id="element-392">
            {formattedTotal}
          </motion.div>
          {bumpSelected && <motion.p initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-sm text-[#2F4538] font-medium mb-2" data-id="element-393">
              Sistema completo + Kit de Herramientas
            </motion.p>}
          <p className="text-[#1a1a1a]/50 font-bold mb-8" data-id="element-394">
            {bumpSelected ? '(Pagás una sola vez)' : '(80% de descuento)'}
          </p>

          <div className="grid sm:grid-cols-2 gap-4 text-left max-w-xl mx-auto mb-10" data-id="element-395">
            {['Acceso inmediato por email (PDF + MP3)', 'Acceso de por vida (sin vencimiento)', 'Actualizaciones gratuitas', 'Garantía incondicional de 7 días'].map((item, i) => <div key={i} className="flex items-start gap-2 text-sm text-[#1a1a1a]/80 font-medium" data-id="element-396">
                <CheckCircle2 className="w-5 h-5 text-[#8B9D83] shrink-0" data-id="element-397" />
                <span data-id="element-398">{item}</span>
              </div>)}
          </div>

          <a href="#" onClick={handleCheckout} className="block w-full bg-[#B4A7D6] text-[#F5F0E8] py-5 rounded-full font-bold text-xl hover:bg-[#9D8FC4] transition-all transform hover:scale-105 shadow-[0_10px_30px_rgba(180,167,214,0.4)] mb-6" data-id="element-399">
            SÍ, QUIERO ACCESO AHORA POR {formattedTotal} →
          </a>

          {/* MercadoLíder badge */}
          <div className="mt-6 flex justify-center" data-id="element-400">
            <img src="https://cdn.magicpatterns.com/uploads/hfCsqLAnsRp4AMvCK4u7iT/MERCADOLIDER.jpg" alt="MercadoLíder Platinum - Pago seguro garantizado con Visa, Mastercard, Maestro y Mercado Pago. 145.121 ventas en los últimos 60 días." width={600} height={600} loading="lazy" decoding="async" className="w-full max-w-md h-auto rounded-2xl" data-id="element-401" />
          </div>
        </motion.div>
      </div>
    </section>;
}